import type { ReactNode } from "react";

import type {
  P8ContinuedSalesPoint,
  P8CumulativeFirstYearPoint,
  P8MonthlyRoasPoint,
  P8MonthlySalesStack,
} from "../../p8OverviewData";
import type { OverviewChartId } from "../../types";

import { CumulativeFirstYearSalesAndSpendChart } from "./CumulativeFirstYearSalesAndSpendChart";
import { ForecastRemainderSalesAndSpendChart } from "./ForecastRemainderSalesAndSpendChart";
import { MonthlyAdSalesBySourceChart } from "./MonthlyAdSalesBySourceChart";
import { MonthlyRoasChart } from "./MonthlyRoasChart";
import { NoForecastSalesAndSpendChart } from "./NoForecastSalesAndSpendChart";

const CHART_CELL_CLASS =
  "flex h-[360px] min-h-[360px] w-full min-w-0 shrink-0 flex-col";

/**
 * Every chart either cell can show, in the order the toolbar lists them. Both
 * slots read this one list, so any chart can be put beside any other, and adding
 * a chart here offers it on both sides at once.
 */
export const OVERVIEW_CHART_OPTIONS: { id: OverviewChartId; label: string }[] =
  [
    {
      id: "no-forecast-monthly",
      label: "[Monthly] Continued ad sales (actuals only)",
    },
    {
      id: "forecast-remainder-monthly",
      label: "[Monthly] Continued ad sales (forecasted)",
    },
    { id: "stacked-sales", label: "[Monthly] Stacked sales" },
    {
      id: "total-cumulative",
      label: "[Cumulative] Continued ad sales (forecasted)",
    },
    { id: "roas", label: "[Monthly] ROAS" },
  ];

type ChartData = {
  continuedSales: P8ContinuedSalesPoint[];
  salesBySource: P8MonthlySalesStack[];
  cumulativeFirstYear: P8CumulativeFirstYearPoint[];
  monthlyRoas: P8MonthlyRoasPoint[];
  /**
   * False on reorder ads: their revenue lands in the month the ad is clicked, so
   * the charts drop every series that describes a cohort maturing — continued
   * sales, the first-year forecast, and the first-year return.
   */
  hasFirstYear: boolean;
  onOpenMonth?: (monthIndex: number) => void;
};

function renderChart(id: OverviewChartId, data: ChartData): ReactNode {
  switch (id) {
    case "total-cumulative":
      return (
        <CumulativeFirstYearSalesAndSpendChart
          data={data.cumulativeFirstYear}
        />
      );
    case "roas":
      return (
        <MonthlyRoasChart
          data={data.monthlyRoas}
          showFirstYear={data.hasFirstYear}
        />
      );
    case "forecast-remainder-monthly":
      return (
        <ForecastRemainderSalesAndSpendChart
          data={data.continuedSales}
          onOpenMonth={data.onOpenMonth}
        />
      );
    case "stacked-sales":
      return <MonthlyAdSalesBySourceChart data={data.salesBySource} />;
    case "no-forecast-monthly":
    default:
      return (
        <NoForecastSalesAndSpendChart
          data={data.continuedSales}
          onOpenMonth={data.onOpenMonth}
          showContinued={data.hasFirstYear}
        />
      );
  }
}

type OverviewChartPairProps = ChartData & {
  leftChart: OverviewChartId;
  rightChart: OverviewChartId;
};

/**
 * The Overview's two side-by-side chart cells. Each side is chosen from the full
 * chart list in the prototype toolbar, so the cuts are no longer locked into
 * fixed pairs.
 */
export function OverviewChartPair({
  leftChart,
  rightChart,
  continuedSales,
  salesBySource,
  cumulativeFirstYear,
  monthlyRoas,
  hasFirstYear,
  onOpenMonth,
}: OverviewChartPairProps) {
  const data: ChartData = {
    continuedSales,
    salesBySource,
    cumulativeFirstYear,
    monthlyRoas,
    hasFirstYear,
    onOpenMonth,
  };

  return (
    <>
      <div className={CHART_CELL_CLASS}>{renderChart(leftChart, data)}</div>
      <div className={CHART_CELL_CLASS}>{renderChart(rightChart, data)}</div>
    </>
  );
}
